import SparkMD5 from 'spark-md5';
import { v4 as uuidv4 } from 'uuid';
import { logError } from '@ardoq/logging';
import {
  ClearAndSetLoadedStatePayload,
  CreatedItemsProcessedPayload,
  EntityPatch,
  LoadedStatesScopeDataPatchedPayload,
  LoadedStateScopeDataPatch,
  LoadedStateUpdatedPayload,
  RegisterLoadedStatePayload,
  SetTraversalIdPayload,
  ToggleCollapsedPathActivePayload,
  ToggleLoadedStateVisibilityPayload,
} from './actions';
import {
  APIComponentAttributes,
  APIScopeData,
  ArdoqId,
  CreatedItemsLoadedState,
  DirectedTriple,
  DirectedTripleWithFilters,
  getContextComponentIds,
  isCreatedItemsLoadedState,
  isTraversalCreatedInViewLoadedState,
  isTraversalLoadedState,
  PersistedLoadedStateParams,
  WithUuid,
  LoadedState,
  TraversalLoadedState,
  LoadedStateHash,
  LoadedStateType,
  Path,
  LoadedStateParams,
  TraversalLoadedStateParams,
  LoadedGraphChunk,
  APIViewpointAttributes,
  ComponentSelection,
  TraversalParams,
  TraversalBase,
  isSearchLoadedState,
  TraversalPathMatchingType,
  WithTransactionId,
  viewpointToTraversalBase,
} from '@ardoq/api-types';
import { isTruthy, toByIdDictionary } from '@ardoq/common-helpers';
import { uniq, uniqBy, partition, pick } from 'lodash';
import { flattenDict } from './flattenDict';
import { toCollapsedPaths } from 'traversals/pathAndGraphUtils';
import { referenceAttributesOperations } from '@ardoq/core';
import { scopeDataOperations } from '@ardoq/scope-data';
import { ExecuteViewpointPayload } from '../viewpointBuilder/actions';
import { pathOperations } from 'traversals/pathOperations';
import { sanitizePathCollapsingRules } from './sanitizePathCollapsingRules';

export type LoadedStatesState = WithTransactionId & {
  loadedStates: LoadedState[];
};

const excludeDuplicateLoadedStates = (
  existingStates: LoadedState[],
  registerStates: LoadedState[]
) => {
  const existingStateHashes = existingStates.map(stateToHash);
  return registerStates.filter(
    state => !existingStateHashes.includes(stateToHash(state))
  );
};

const normalizeTriple = (
  triple: DirectedTripleWithFilters
): DirectedTripleWithFilters => ({
  ...triple,
  // Make sure that the properties exist on the object, even if they are
  // undefined to ensure consistent hashing.
  sourceFilter: triple.sourceFilter,
  targetFilter: triple.targetFilter,
  referenceFilter: triple.referenceFilter,
});

const normalizePath = (path: DirectedTriple[]): DirectedTriple[] =>
  path.map(normalizeTriple);

const isNonEmptyPath = (path: DirectedTriple[]): boolean => path.length > 0;

/**
 * The traversal-definition fields shared by every traversal `data` shape
 * (i.e. `TraversalBase` — everything except `componentSelection`). Reconstructing
 * a traversal LoadedState's `data` from an explicit field list is done in
 * several places; routing them through this picker means a new traversal field
 * only has to be added here, not remembered at every call site.
 *
 * `startSetExpansion` is only included when set, so the data shape (and the
 * derived `loadedStateHash`) is unchanged for traversals without expansion.
 */
const pickTraversalBaseFields = (data: TraversalBase): TraversalBase => ({
  paths: data.paths,
  filters: data.filters,
  pathMatching: data.pathMatching,
  pathCollapsingRules: sanitizePathCollapsingRules(data.pathCollapsingRules),
  ...(data.startSetExpansion
    ? {
        startSetExpansion: data.startSetExpansion,
        startSetExpansionFromType: data.startSetExpansionFromType,
      }
    : {}),
  ...(data.convertedFrom && { convertedFrom: data.convertedFrom }),
});

/**
 * Maps the persisted-only variants onto their runtime counterparts.
 * `traversalCreatedInView` is structurally a traversal without a `traversalId`
 * (`Traversal` aliases `TraversalParams`), and a `search` is a traversal with no
 * hops, so neither loses anything.
 */
const toRuntimeType = (
  loadedState: PersistedLoadedStateParams
): LoadedStateParams => {
  if (isTraversalCreatedInViewLoadedState(loadedState)) {
    return { ...loadedState, type: LoadedStateType.TRAVERSAL };
  }
  if (isSearchLoadedState(loadedState)) {
    return {
      ...loadedState,
      type: LoadedStateType.TRAVERSAL,
      data: toTraversalParamsForComponentSelection(
        loadedState.data.componentSelection
      ),
    };
  }
  return loadedState;
};

/**
 * Rebuilds `data` from an explicit field list, so stored states cannot carry
 * fields the model no longer has. Also on the write path.
 */
export const normalizeLoadedState = <T extends LoadedState | LoadedStateParams>(
  loadedState: T
): T => {
  if (isTraversalLoadedState(loadedState)) {
    const { data } = loadedState;
    return {
      ...loadedState,
      data: {
        ...pickTraversalBaseFields(data),
        paths: data.paths.map(normalizePath).filter(isNonEmptyPath),
        componentSelection: {
          // ?? [] is intentional: pre-refactor API data may lack startSet
          // for query-mode entries. Normalizing at ingestion keeps the
          // strict ComponentSelection type honest for the rest of the app.
          startSet: data.componentSelection.startSet ?? [],
          ...(data.componentSelection.startQuery && {
            startQuery: data.componentSelection.startQuery,
          }),
        },
      },
    };
  }
  return loadedState;
};

const reduceRegisterLoadedState = (
  state: LoadedStatesState,
  { loadedStates }: RegisterLoadedStatePayload
): LoadedStatesState => ({
  ...state,
  loadedStates: [
    ...state.loadedStates,
    ...excludeDuplicateLoadedStates(
      state.loadedStates,
      loadedStates.map(normalizeLoadedState)
    ),
  ],
  transactionId: undefined,
});

const reduceClearAndSetLoadedState = (
  state: LoadedStatesState,
  { loadedStates, transactionId }: ClearAndSetLoadedStatePayload
): LoadedStatesState => ({
  ...state,
  loadedStates: loadedStates.map(normalizeLoadedState),
  transactionId,
});

const reduceClearLoadedState = (
  state: LoadedStatesState
): LoadedStatesState => ({
  ...state,
  loadedStates: [],
  transactionId: undefined,
});

// called after saving a new traversal and receiving id from server
const reduceSetTraversalId = (
  state: LoadedStatesState,
  { id: traversalId, loadedStateHash }: SetTraversalIdPayload
): LoadedStatesState => ({
  ...state,
  loadedStates: state.loadedStates.map(loadedState => {
    if (
      stateToHash(loadedState) === loadedStateHash &&
      isTraversalLoadedState(loadedState)
    ) {
      return { ...loadedState, traversalId } satisfies TraversalLoadedState;
    }
    return loadedState;
  }),
  transactionId: undefined,
});
const reduceToggleLoadedStateVisibility = (
  state: LoadedStatesState,
  { loadedStateHash }: ToggleLoadedStateVisibilityPayload
): LoadedStatesState => ({
  ...state,
  loadedStates: state.loadedStates.map(loadedState => {
    if (stateToHash(loadedState) === loadedStateHash) {
      return {
        ...loadedState,
        isHidden: !loadedState.isHidden,
      };
    }
    return loadedState;
  }),
  transactionId: undefined,
});

const reduceDeleteLoadedState = (
  state: LoadedStatesState,
  loadedStateHash: LoadedStateHash
): LoadedStatesState => ({
  ...state,
  loadedStates: state.loadedStates.filter(
    loadedState => stateToHash(loadedState) !== loadedStateHash
  ),
  transactionId: undefined,
});

/**
 * Atomic update of loaded state because params cannot be updated separately
 * as the loadedGraph/scopeData would not match.
 */
const replaceExistingLoadedState = (
  state: LoadedStatesState,
  {
    existingHash,
    loadedState,
    isMainLoadedState = false,
  }: LoadedStateUpdatedPayload
): LoadedStatesState => {
  if (isMainLoadedState) {
    return {
      ...state,
      loadedStates: [loadedState, ...state.loadedStates.slice(1)],
      transactionId: undefined,
    };
  }
  return {
    ...state,
    loadedStates: state.loadedStates.map(existingState => {
      if (stateToHash(existingState) === existingHash) {
        return loadedState;
      }
      return existingState;
    }),
    transactionId: undefined,
  };
};

const applyEntityPatch = <T extends { _id: ArdoqId }>(
  collection: T[],
  patch: EntityPatch<T> | undefined
): T[] => {
  if (!patch) return collection;
  const { created, updated, deletedIds } = patch;
  const hasCreated = created && Object.keys(created).length > 0;
  const hasUpdated = updated && Object.keys(updated).length > 0;
  const hasDeleted = deletedIds && deletedIds.length > 0;
  if (!hasCreated && !hasUpdated && !hasDeleted) return collection;

  const deletedSet = hasDeleted ? new Set(deletedIds) : null;
  const next = collection.reduce<T[]>((acc, item) => {
    if (deletedSet?.has(item._id)) return acc;
    acc.push(updated?.[item._id] ?? item);
    return acc;
  }, []);
  if (hasCreated) next.push(...Object.values(created));
  return next;
};

const applyScopeDataPatch = (
  scopeData: APIScopeData,
  patch: LoadedStateScopeDataPatch
): APIScopeData => ({
  ...scopeData,
  components: applyEntityPatch(scopeData.components, patch.components),
  references: applyEntityPatch(scopeData.references, patch.references),
  workspaces: applyEntityPatch(scopeData.workspaces, patch.workspaces),
  models: applyEntityPatch(scopeData.models, patch.models),
  fields: applyEntityPatch(scopeData.fields, patch.fields),
  tags: applyEntityPatch(scopeData.tags, patch.tags),
});

const reduceLoadedStatesScopeDataPatched = (
  state: LoadedStatesState,
  { partials }: LoadedStatesScopeDataPatchedPayload
): LoadedStatesState => {
  if (Object.keys(partials).length === 0) return state;
  return {
    ...state,
    loadedStates: state.loadedStates.map(loadedState => {
      const patch = partials[stateToHash(loadedState)];
      if (!patch) return loadedState;
      // Only scopeData is patched. componentIdsAndReferences is the BE's
      // traversal-result envelope (ids, pathResults, startSetResult,
      // parentChildReferences, reference-attribute bag) and only the BE has
      // the authority to rewrite it — that's the reload path via
      // rebuildActiveState. The patch classification fires precisely when no
      // structural field (parent / source / target / typeId / filter-affecting
      // attrs) changed, so the envelope stays correct. Consumers that need
      // fresh attributes look entities up in scopeData by id.
      return {
        ...loadedState,
        scopeData: applyScopeDataPatch(loadedState.scopeData, patch),
      };
    }),
    transactionId: undefined,
  };
};

const addDataToLoadedStateSearch = (
  loadedState: CreatedItemsLoadedState,
  newCreatedItemsLoadedState: CreatedItemsLoadedState
) => {
  const componentIds = uniq([
    ...loadedState.data.componentIds,
    ...newCreatedItemsLoadedState.data.componentIds,
  ]);
  const referenceIds = uniq([
    ...loadedState.data.referenceIds,
    ...newCreatedItemsLoadedState.data.referenceIds,
  ]);
  // The freshly created/fetched items come first so that on overlapping ids
  // their (newer) attributes win over the existing loaded state — uniqBy and
  // scopeDataOperations.merge both keep the first occurrence.
  const references = uniqBy(
    [
      ...(newCreatedItemsLoadedState.componentIdsAndReferences?.references ??
        []),
      ...(loadedState.componentIdsAndReferences?.references ?? []),
    ],
    '_id'
  );

  const parentChildReferences = uniqBy(
    [
      ...(newCreatedItemsLoadedState.componentIdsAndReferences
        ?.parentChildReferences ?? []),
      ...(loadedState.componentIdsAndReferences?.parentChildReferences ?? []),
    ],
    '_id'
  );

  // Merge the scope data too, otherwise the newly created entities' attributes
  // (model, type, ...) are dropped and the loaded state's scopeData goes out of
  // sync with its componentIds/referenceIds. This matters now that the loaded
  // state's scopeData is the source for the enhanced scope data on loadedGraph.
  const scopeData = scopeDataOperations.merge(
    newCreatedItemsLoadedState.scopeData,
    loadedState.scopeData
  );

  return {
    ...loadedState,
    scopeData,
    componentIdsAndReferences: {
      componentIds,
      references,
      parentChildReferences,
      startSetResult: componentIds,
      pathResults: [],
    },
    data: {
      ...loadedState.data,
      referenceIds,
      componentIds,
    },
  };
};

const traversalParamsFromSelectionAndPaths = (
  componentSelection: ComponentSelection,
  paths: Path[] = []
): TraversalLoadedStateParams => {
  const base = {
    paths,
    filters: {},
    pathMatching: TraversalPathMatchingType.LOOSE,
    pathCollapsingRules: [],
  };
  const data: TraversalParams = { ...base, componentSelection };
  return {
    data,
    isHidden: false,
    type: LoadedStateType.TRAVERSAL,
  };
};

const reduceUpdateOrCreateLoadedStateOnItemCreated = (
  state: LoadedStatesState,
  createdItemsLoadedState: CreatedItemsLoadedState
): LoadedStatesState => {
  const hasCreatedItemsLoadedState = state.loadedStates.some(
    isCreatedItemsLoadedState
  );
  if (!hasCreatedItemsLoadedState) {
    return {
      ...state,
      loadedStates: [...state.loadedStates, createdItemsLoadedState],
    };
  }
  return {
    ...state,
    loadedStates: state.loadedStates.map(loadedState => {
      if (isCreatedItemsLoadedState(loadedState)) {
        return addDataToLoadedStateSearch(loadedState, createdItemsLoadedState);
      }
      return loadedState;
    }),
    transactionId: undefined,
  };
};

const getLoadedStateWithHash = <T extends LoadedStateParams>(
  state: T[],
  loadedStateHash: LoadedStateHash
) => {
  return state.find(
    loadedState => stateToHash(loadedState) === loadedStateHash
  );
};

const getTraversalWithLoadedStateHash = (
  state: LoadedState[],
  loadedStateHash: LoadedStateHash
): TraversalLoadedState | null => {
  const loadedState = getLoadedStateWithHash(state, loadedStateHash);
  if (!loadedState) return null;
  if (isTraversalLoadedState(loadedState)) return loadedState;
  return null;
};

const stateToHash = (loadedState: LoadedState | LoadedStateParams) => {
  return SparkMD5.hash(
    flattenDict(normalizeLoadedState(loadedState).data ?? {}).join('')
  );
};

const toPathsWithStartSetResultsAndCollapsedPaths = (state: LoadedState[]) =>
  state.map(loadedState => {
    if (isTraversalLoadedState(loadedState)) {
      const paths = loadedState.data.paths;
      const hasActiveCollapsingRules =
        loadedState.data.pathCollapsingRules?.some(({ isActive }) => isActive);
      const collapsedPaths = loadedState.data.pathCollapsingRules
        ? toCollapsedPaths(
            paths,
            loadedState.data.pathCollapsingRules.filter(
              ({ isActive }) => isActive
            )
          )
        : paths;

      return {
        paths,
        collapsedPaths,
        startSetResult:
          loadedState.componentIdsAndReferences?.startSetResult ?? [],
        pathResults: loadedState.componentIdsAndReferences?.pathResults ?? [],
        hasActiveCollapsingRules,
        isConvertedFromWorkspaceMode:
          loadedState.data.convertedFrom === 'workspaceMode',
      };
    }
    return {
      paths: [],
      collapsedPaths: [],
      startSetResult:
        loadedState.componentIdsAndReferences?.startSetResult ?? [],
      pathResults: loadedState.componentIdsAndReferences?.pathResults ?? [],
      hasActiveCollapsingRules: false,
      isConvertedFromWorkspaceMode: false,
    };
  });

const getTraversalParamsFromViewpoint = (
  viewpoint: APIViewpointAttributes,
  componentIds: ArdoqId[]
): TraversalLoadedStateParams => {
  return {
    type: LoadedStateType.TRAVERSAL,
    isHidden: false,
    traversalId: viewpoint._id,
    data: {
      componentSelection: {
        startSet: componentIds,
      },
      ...viewpointToTraversalBase(viewpoint),
    },
  };
};

function updateTraversalContextComponents(
  previousLoadedState: TraversalLoadedState,
  componentSelection: ComponentSelection
): TraversalLoadedStateParams & WithUuid {
  // Reuse the whole traversal definition (incl. start set expansion) when
  // switching the context start set.
  const base = pickTraversalBaseFields(previousLoadedState.data);
  const data: TraversalParams = { ...base, componentSelection };
  return {
    data,
    isHidden: previousLoadedState.isHidden,
    type: previousLoadedState.type,
    traversalId: previousLoadedState.traversalId,
    uuid: previousLoadedState.uuid,
  };
}

const componentIdIsIncludedInLoadedState = (
  loadedStates: LoadedState[],
  componentId: ArdoqId
) => {
  return loadedStates
    .filter(loadedState => !loadedState.isHidden)
    .some(
      loadedState =>
        loadedState.componentIdsAndReferences?.componentIds.includes(
          componentId
        ) ?? false
    );
};

const toggleCollapsedPathActive = (
  state: LoadedStatesState,
  { rule, loadedStateHash }: ToggleCollapsedPathActivePayload
): LoadedStatesState => ({
  ...state,
  loadedStates: state.loadedStates.map(loadedState => {
    if (
      stateToHash(loadedState) === loadedStateHash &&
      isTraversalLoadedState(loadedState)
    ) {
      return {
        ...loadedState,
        data: {
          ...loadedState.data,
          pathCollapsingRules: loadedState.data.pathCollapsingRules.map(
            currentRule => {
              if (currentRule.referenceTypeId === rule.referenceTypeId) {
                return {
                  ...currentRule,
                  isActive: !currentRule.isActive,
                };
              }
              return currentRule;
            }
          ),
        },
      };
    }
    return loadedState;
  }),
});

const getLoadedStateParamsFromLoadedState = <T extends LoadedStateParams>(
  loadedState: T
): LoadedStateParams => {
  // Note: Each case must be explicit due to TypeScript's strict union type checking
  // Destructure properties from each state entry to prevent extraneous data from being
  // included in `LoadedStateParams`. This ensures the returned object only
  // contains properties relevant to its specific subtype, avoiding issues
  // if a state entry's type changes (e.g., from 'traversal' to 'search').

  if (isTraversalLoadedState(loadedState)) {
    const { data, type, isHidden, traversalId } = loadedState;
    return {
      data: {
        ...pickTraversalBaseFields(data),
        componentSelection: data.componentSelection,
      },
      type,
      isHidden,
      traversalId,
    };
  }

  if (isCreatedItemsLoadedState(loadedState)) {
    const {
      data: { componentIds, referenceIds },
      type,
      isHidden,
    } = loadedState;

    return { data: { componentIds, referenceIds }, type, isHidden };
  }

  loadedState satisfies never;
  logError(
    Error('Unhandled loaded state type when stripping runtime-only fields')
  );
  const { data, type, isHidden } = loadedState as LoadedStateParams;
  return { data, type, isHidden } as LoadedStateParams;
};

// prettier-ignore
function attachLoadedGraph<T extends LoadedState>(loadedState: T, loadedGraph: LoadedGraphChunk): T;
// prettier-ignore
function attachLoadedGraph<T extends LoadedStateParams>(loadedState: T, loadedGraph: LoadedGraphChunk): T & LoadedGraphChunk & WithUuid;
// prettier-ignore
function attachLoadedGraph(loadedState: LoadedStateParams, {scopeData, componentIdsAndReferences}: LoadedGraphChunk): LoadedState {
  return {
    ...loadedState,
    scopeData,
    componentIdsAndReferences,
    uuid: (loadedState as Partial<LoadedState>).uuid ?? uuidv4(),
  };
}

const getEmptyLoadedGraph = (): LoadedGraphChunk => ({
  scopeData: scopeDataOperations.getEmpty(),
  componentIdsAndReferences: {
    componentIds: [],
    references: [],
    parentChildReferences: [],
    startSetResult: [],
    pathResults: [],
  },
});

const fromExecuteViewpointPayload = (
  payload: ExecuteViewpointPayload,
  traversalId: string | undefined
): TraversalLoadedStateParams => {
  return {
    data: {
      ...pickTraversalBaseFields(payload),
      componentSelection: payload.componentSelection,
    } as TraversalParams,
    isHidden: false,
    type: LoadedStateType.TRAVERSAL,
    traversalId,
  };
};

const createdItemsProcessed = (
  state: LoadedStatesState,
  { componentIds, referenceIds }: CreatedItemsProcessedPayload
): LoadedStatesState => {
  const { loadedStates: states } = state;
  const [[createdItemsLoadedState], otherLoadedStates] = partition(
    states,
    loadedState => isCreatedItemsLoadedState(loadedState)
  );
  if (!createdItemsLoadedState) {
    return state;
  }

  const createdItemsLoadedStateIndex = states.indexOf(createdItemsLoadedState);

  const { data, componentIdsAndReferences } = createdItemsLoadedState;
  const processedReferenceIdsSet = new Set(referenceIds);
  const processedComponentIdsSet = new Set(componentIds);
  const updatedData = {
    componentIds: data.componentIds.filter(
      id => !processedComponentIdsSet.has(id)
    ),
    referenceIds: data.referenceIds.filter(
      id => !processedReferenceIdsSet.has(id)
    ),
  };

  if (
    updatedData.componentIds.length === 0 &&
    updatedData.referenceIds.length === 0
  ) {
    return { ...state, loadedStates: otherLoadedStates };
  }

  const updatedComponentIdsAndReferences = {
    componentIds: updatedData.componentIds,
    references: componentIdsAndReferences.references.filter(
      ({ _id }) => !processedReferenceIdsSet.has(_id)
    ),
    parentChildReferences:
      componentIdsAndReferences.parentChildReferences.filter(
        ({ _id }) => !processedReferenceIdsSet.has(_id)
      ),
    startSetResult: updatedData.componentIds,
    pathResults: [],
  };

  const updatedCreatedItemsLoadedState = {
    ...createdItemsLoadedState,
    scopeData: {
      ...createdItemsLoadedState.scopeData,
      scopeComponents: updatedData.componentIds,
    },
    componentIdsAndReferences: updatedComponentIdsAndReferences,
    data: updatedData,
  };

  return {
    ...state,
    loadedStates: [
      ...otherLoadedStates.slice(0, createdItemsLoadedStateIndex),
      updatedCreatedItemsLoadedState,
      ...otherLoadedStates.slice(createdItemsLoadedStateIndex),
    ],
  };
};

/**
 * A traversal with no hops. Returns a fresh object every call, so callers cannot
 * share the `paths` and `pathCollapsingRules` arrays.
 */
const toTraversalParamsForComponentSelection = (
  componentSelection: ComponentSelection
): TraversalParams => ({
  componentSelection,
  paths: [],
  filters: {},
  pathMatching: TraversalPathMatchingType.LOOSE,
  pathCollapsingRules: [],
});

/**
 * The traversal params that execute a loaded state. A created-items state holds
 * its components directly, so it becomes a traversal over them with no hops.
 */
const toTraversalParams = (loadedState: LoadedStateParams): TraversalParams =>
  isTraversalLoadedState(loadedState)
    ? loadedState.data
    : toTraversalParamsForComponentSelection({
        startSet: loadedState.data.componentIds,
      });

const toTraversalLoadedState = (
  loadedState: LoadedState
): TraversalLoadedState => {
  if (isTraversalLoadedState(loadedState)) {
    return loadedState;
  }
  return {
    ...loadedState,
    type: LoadedStateType.TRAVERSAL,
    traversalId: undefined,
    data: toTraversalParams(loadedState),
  } as TraversalLoadedState;
};

const getAllTriplesFromPaths = (
  loadedState: TraversalLoadedState
): DirectedTriple[] =>
  uniqBy(
    loadedState.data.paths.flatMap(path =>
      path.map(triple =>
        pick(triple, ['sourceType', 'targetType', 'referenceType', 'direction'])
      )
    ),
    ({ sourceType, targetType, referenceType, direction }) =>
      `${sourceType}-${targetType}-${referenceType}-${direction}`
  );

const getStartContextComponentType = (
  loadedState: LoadedState
): string | null => {
  return isTraversalLoadedState(loadedState)
    ? pathOperations.getStartTypeFromPaths(loadedState.data.paths)
    : null;
};

/**
 * The distinct component types a component selection currently resolves to,
 * given the already-fetched start-set result and components it resolved
 * against (e.g. from a `TraversalLoadedState` or a freshly built
 * `LoadedGraphChunk`).
 */
const getContextComponentTypeNames = (
  componentSelection: ComponentSelection,
  startSetResult: ArdoqId[],
  components: APIComponentAttributes[]
): string[] => {
  const contextComponentIds = getContextComponentIds(
    componentSelection,
    startSetResult
  );
  const componentsById = toByIdDictionary(components);
  return [
    ...new Set(
      contextComponentIds
        .map(componentId => componentsById[componentId]?.type)
        .filter(isTruthy)
    ),
  ];
};

export const toNormalizedLoadedStateParams = (
  loadedStates: LoadedStateParams[]
): LoadedStateParams[] =>
  loadedStates
    .map(getLoadedStateParamsFromLoadedState)
    .map(normalizeLoadedState);

/**
 * The persisted -> runtime boundary. Everything read out of a slide, a bookmark
 * or a URL goes through here. `PersistedLoadedStateParams` and
 * `LoadedStateParams` are deliberately distinct types so that a read site which
 * forgets this is a compile error rather than a review comment.
 *
 * Because the read maps the type, a state re-saved later persists as
 * `traversal` — legacy data migrates without a backfill.
 */
export const fromPersistedLoadedState = (
  loadedState: PersistedLoadedStateParams
): LoadedStateParams => normalizeLoadedState(toRuntimeType(loadedState));

const getScopeComponentIds = (loadedState: LoadedState): ArdoqId[] =>
  loadedState.scopeData.scopeComponents;

const getReferenceIds = (loadedState: LoadedState) => {
  if (loadedState.componentIdsAndReferences) {
    return loadedState.componentIdsAndReferences.references.map(
      ({ _id }) => _id
    );
  }
  if (isCreatedItemsLoadedState(loadedState)) {
    return loadedState.data.referenceIds;
  }
  return [];
};

const getFEParentChildReferences = (loadedState: LoadedState) =>
  (loadedState.componentIdsAndReferences?.parentChildReferences ?? []).map(
    referenceAttributesOperations.toFEParentChildFormat
  );

const isSavedTraversal = (loadedState: LoadedState): boolean =>
  isTraversalLoadedState(loadedState) && Boolean(loadedState.traversalId);

/**
 * Whether any of the loaded states is a traversal converted from a
 * workspace-mode slide.
 */
const isConvertedFromWorkspaceMode = (
  loadedStates: (LoadedState | LoadedStateParams | PersistedLoadedStateParams)[]
): boolean =>
  loadedStates.some(
    loadedState =>
      loadedState.type === LoadedStateType.TRAVERSAL &&
      loadedState.data.convertedFrom === 'workspaceMode'
  );

export const loadedStateOperations = {
  getTraversalParamsFromViewpoint,
  updateTraversalContextComponents,
  pickTraversalBaseFields,
  normalizePath,
  reduceRegisterLoadedState,
  reduceClearAndSetLoadedState,
  reduceClearLoadedState,
  reduceSetTraversalId,
  reduceToggleLoadedStateVisibility,
  reduceDeleteLoadedState,
  replaceExistingLoadedState,
  reduceLoadedStatesScopeDataPatched,
  applyScopeDataPatch,
  reduceUpdateOrCreateLoadedStateOnItemCreated,
  attachLoadedGraph,
  getEmptyLoadedGraph,
  fromExecuteViewpointPayload,
  getTraversalWithLoadedStateHash,
  getLoadedStateWithHash,
  stateToHash,
  toPathsWithStartSetResultsAndCollapsedPaths,
  componentIdIsIncludedInLoadedState,
  toggleCollapsedPathActive,
  getLoadedStateParamsFromLoadedState,
  createdItemsProcessed,
  toTraversalLoadedState,
  toTraversalParams,
  getAllTriplesFromPaths,
  getStartContextComponentType,
  getContextComponentTypeNames,
  toNormalizedLoadedStateParams,
  traversalParamsFromSelectionAndPaths,
  getScopeComponentIds,
  getReferenceIds,
  getFEParentChildReferences,
  isSavedTraversal,
  isConvertedFromWorkspaceMode,
};
